import { useInfiniteQuery } from "@tanstack/react-query";
import { useState, useEffect, useMemo, useCallback } from "react";
import { useDebounce } from "use-debounce";
import {
  FORMATION_COLLABORATORS_PAGE_SIZE,
  RefreshReactQuery,
} from "../constants/AppConstants";
import { ERROR_LABEL, ERROR_MESSAGE_LABEL } from "../constants/Dictionary";
import CollaboratorsService from "../services/api/collaborators/Collaborators.service";
import { useNotification } from "./notification.hook";

interface useFormationCollaboratorsProps {
  queryKey: string;
}

export const useFormationCollaborators = ({
  queryKey,
}: useFormationCollaboratorsProps) => {
  const { notification } = useNotification();
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, 500);

  const { data, isLoading, isFetching, hasNextPage, fetchNextPage, isError } =
    useInfiniteQuery({
      queryKey: [queryKey, debouncedSearch],
      queryFn: ({ pageParam, signal }) =>
        CollaboratorsService.list(
          FORMATION_COLLABORATORS_PAGE_SIZE,
          pageParam as number,
          [
            { key: "showSelf", value: [true] },
            ...(debouncedSearch
              ? [{ key: "search", value: [debouncedSearch] }]
              : []),
          ],
          signal,
        ),
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) =>
        allPages.length * FORMATION_COLLABORATORS_PAGE_SIZE <
        lastPage.pagination.total
          ? allPages.length
          : undefined,
      staleTime: RefreshReactQuery.MODERATE,
    });

  useEffect(() => {
    if (isError) notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
  }, [isError]);

  const items = useMemo(
    () => data?.pages.flatMap((page) => page.data) ?? [],
    [data],
  );

  const fetchMore = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  return {
    data: items,
    initLoading: isLoading,
    canLoadMore: hasNextPage ?? false,
    fetchMore,
    fetchingMore: isFetching && (data?.pages.length ?? 0) > 0,
    search,
    setSearch,
  };
};
